import { useState } from 'react';
import {
  refundLineStatusLabel,
  type LedgerOrderItem,
  type LedgerOrderLine,
  type OrderRefundResponse,
} from '@cuito/types';
import { Button, Input, Modal, orderNoShort } from '@cuito/ui';
import { ledgerApi, type RefundRequest } from '../../api/ledger';
import { readErrorCode, readServerDetails, readServerMessage } from '../../lib/apiError';
import { newIdempotencyKey } from '../../lib/idempotencyKey';
import { AMOUNT_UNAVAILABLE_TEXT, formatServerYuan } from '../../lib/schemeForm';

/** 三档退款范围（契约 §13.6 的两种 `type`，其中 `PARTIAL` 按行 / 按量两种录入方式） */
type Scope = 'FULL' | 'LINES' | 'QUANTITIES';

const SCOPES: ReadonlyArray<{ value: Scope; label: string }> = [
  { value: 'FULL', label: '整单' },
  { value: 'LINES', label: '按行' },
  { value: 'QUANTITIES', label: '按量' },
];

const REASON_LABEL = '退款原因（2–200 字）';

/**
 * 业务错误码 → 中文（主计划 §5 Task 12 的硬要求；文案逐字取自附录 A25 / UI 规范 §3.2 `/refunds` 行）。
 * 其余一律用服务端原文（本仓既有口径：`readServerMessage`），连原文都没有才兜底。
 *
 * 为什么要翻这三句而不是直接显示服务端 `message`：
 *   · `REFUND_EXCEEDS_QUANTITY` 的 `details.remaining` 是「还剩几件」的**唯一**信息，
 *     丢了它店员只能试错；
 *   · `ORDER_INVALID_TRANSITION` + `details.status === 'REFUNDED'` 与「订单还没交付」
 *     是两件事，一句通用文案会让用户不知道其实已经退完了；
 *   · `IDEMPOTENCY_KEY_REUSED` 的原文对店员是黑话（而且它同时意味着「**这笔没退成**」）。
 */
export function refundErrorMessage(error: unknown): string {
  const code = readErrorCode(error);
  if (code === 'REFUND_EXCEEDS_QUANTITY') {
    const remaining = readServerDetails(error)?.remaining;
    // §13.6：整单退款而全行无可退时 `details.reason === 'NOTHING_TO_REFUND'`（没有 `remaining`）
    return typeof remaining === 'number' ? `退款数量不能超过 ${remaining} 件` : '没有可退款的商品';
  }
  if (code === 'ORDER_INVALID_TRANSITION') {
    return readServerDetails(error)?.status === 'REFUNDED' ? '订单已全额退款' : '订单当前不可退款';
  }
  if (code === 'IDEMPOTENCY_KEY_REUSED') return '退款请求重复，请关闭后重新发起';
  return readServerMessage(error) ?? '退款失败，请重试';
}

/**
 * 该行的**可退量**：`quantity − shortQty − refundedQty`（契约 §13.6「可退量的定义」，附录 A27）。
 *
 * **`shortQty` 必须扣掉**：缺货单位在 `POST /orders/:id/short` 时已经还回可售池、并从
 * `Order.totalAmount` 里减掉了，顾客**从未为它们付钱** —— 少扣一项就是**多退钱**
 * （`quantity 3 / shortQty 1 / unitPrice 45.00` 的单实收 90.00，按 `quantity` 退却退 135.00）。
 */
function remainingOf(line: LedgerOrderLine): number {
  return line.quantity - line.shortQty - line.refundedQty;
}

/** 确认阶段的一句话摘要（金额不在这里算：UI 规范 §6.4 规则 1「金额不由前端计算」） */
function summaryOf(body: RefundRequest, scope: Scope, order: LedgerOrderItem): string {
  if (body.type === 'FULL') return `整单退款 · 订单 ${orderNoShort(order.orderNo)}`;
  const lines = body.lines ?? [];
  const quantity = lines.reduce((sum, item) => sum + item.quantity, 0);
  return scope === 'LINES'
    ? `按行退款 ${lines.length} 行（共 ${quantity} 件） · 订单 ${orderNoShort(order.orderNo)}`
    : `按量退款 ${quantity} 件 · 订单 ${orderNoShort(order.orderNo)}`;
}

interface Props {
  order: LedgerOrderItem;
  onClose: () => void;
  /** 退款成功（含同键重放）后回调：页面负责关弹窗、提示、重取 */
  onRefunded: (result: OrderRefundResponse) => void;
}

interface Row {
  line: LedgerOrderLine;
  /** 可退量（数量算术，**不是**状态判据） */
  remaining: number;
}

/**
 * 退款弹窗（契约 §13.6；UI 规范 §1.4 的行内确认弹窗 + §3.2 `/refunds` 行）。
 *
 * ## 两个阶段
 *
 * ① **选择阶段**（`Modal`，标题「退款」）：整单 / 按行 / 按量 + 退款原因；
 * ② **行内确认阶段**（UI 规范 `:186-190` 的模板逐字复用）：`danger` 确认 + `secondary` 取消。
 * 不可逆操作必须二次确认，且**禁止 `window.confirm`**（`05-ui-design-system.md:190`）。
 * 第一阶段**一个请求都不发**：本地能判的错（原因长度、选没选行、数量上界）先在本地拦下，
 * 免得用户拿一个必然 400 的请求去换服务端的通用文案。
 *
 * ## 幂等键的所有权
 *
 * 键在本组件**挂载时**生成一次，本次挂载内的每次提交（含失败后重试）都用它。
 * 页面用 `key={orderId:nonce}` 重挂载本组件 ⇒ **重新打开 = 新键**。
 * 反过来（每次点击生成键）会让「网络超时后重试」变成两次真实退款。
 *
 * ## 按量档的语义：**未填 = 不参与**（控制器裁决 12b，资金安全）
 *
 * 按量输入**初始为空**（占位符给出上限），空 = 这一行**不参与**本次退款；显式填 0 / 非整数 / 负数
 * 才是录入错误（`退款数量至少 1 件`），参与行合计数为 0 ⇒ `请至少选择一行`。
 *
 * 为什么默认值**不能**是「这一行的剩余可退量」：收银员想只退 A 行 1 件时，打开按量档、在 A 填 1，
 * B 行会被**静默**按它自己的全部剩余量一起退掉（确认框的合计里会包含 B）——「默认就退满其它行」
 * 是拿钱冒险的默认值。默认必须是「什么都不退」。
 *
 * ## 三个可退量相关的界面行为（A27 的资金正确性）
 *
 *   · 「最多可退 N 件」= `remainingOf(line)`（输入框占位符 + 行内常显提示），输入的 `max` 同值；
 *   · 可退量为 0 的行：勾选框与数量输入**禁用** + 标「已退完」（不让它提交上去换 400）；
 *   · 全部行都为 0 ⇒ 表单整体不出现，只说「订单已全额退款」。
 *
 * ## 判据只有一处
 *
 * 行级文案一律 `refundLineStatusLabel(refundedQty, quantity, shortQty)`（契约 §13.8 的唯一实现），
 * 本文件**不写** `refundedQty === quantity` 这类比较 —— 那在正常数据下与唯一实现逐字等价，
 * 只有异常数据才分得出对错（静态门在 `RefundDialog.spec.tsx` ⑳）。
 */
export default function RefundDialog({ order, onClose, onRefunded }: Props) {
  const [scope, setScope] = useState<Scope>('FULL');
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, string>>({});
  const [pending, setPending] = useState<RefundRequest | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 一次性取键：本次挂载内所有提交（含重试）共用它（见文件头「幂等键的所有权」） */
  const [idempotencyKey] = useState(() => newIdempotencyKey());

  const rows: Row[] = order.lines.map((line) => ({ line, remaining: remainingOf(line) }));
  const allExhausted = rows.length > 0 && rows.every((row) => row.remaining <= 0);

  const buildRequest = (): RefundRequest | null => {
    const trimmed = reason.trim();
    if (trimmed.length < 2 || trimmed.length > 200) {
      setError('退款原因需要 2 到 200 个字');
      return null;
    }
    if (scope === 'FULL') return { type: 'FULL', reason: trimmed };

    const lines: Array<{ orderLineId: string; quantity: number }> = [];
    for (const row of rows) {
      // 可退量为 0 的行一律不参与（界面已禁用，这里是第二道防线）
      if (row.remaining <= 0) continue;
      if (scope === 'LINES') {
        if (checked[row.line.orderLineId] !== true) continue;
        lines.push({ orderLineId: row.line.orderLineId, quantity: row.remaining });
        continue;
      }
      // 按量（控制器裁决 12b）：**空 = 不参与**（不是错误）；显式填 0 / 非整数 / 负数才是录入错误。
      // 这里**绝不能**回落成 `String(row.remaining)`：那会让「只想退 A 行 1 件」连带退掉 B 行的全部剩余量。
      const draft = (quantityDrafts[row.line.orderLineId] ?? '').trim();
      if (draft === '') continue;
      const value = Number(draft);
      if (!Number.isInteger(value) || value < 1) {
        setError('退款数量至少 1 件');
        return null;
      }
      if (value > row.remaining) {
        setError(`退款数量不能超过 ${row.remaining} 件`);
        return null;
      }
      lines.push({ orderLineId: row.line.orderLineId, quantity: value });
    }
    if (lines.length === 0) {
      setError('请至少选择一行');
      return null;
    }
    return { type: 'PARTIAL', reason: trimmed, lines };
  };

  const openConfirm = (): void => {
    setError(null);
    const body = buildRequest();
    if (body === null) return;
    setPending(body);
    setConfirming(true);
  };

  const submit = async (): Promise<void> => {
    if (pending === null || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await ledgerApi.refund(order.orderId, pending, idempotencyKey);
      onRefunded(result);
    } catch (e) {
      setError(refundErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="退款" maxWidth="max-w-lg">
      <div className="flex flex-col gap-4" data-testid="refund-dialog">
        <p className="text-sm text-gray-500">
          订单 {orderNoShort(order.orderNo)} · {order.lines.length} 行 · 已退{' '}
          {formatServerYuan(order.refundedAmount) ?? AMOUNT_UNAVAILABLE_TEXT}
        </p>

        {allExhausted ? (
          <p className="text-lg font-bold" data-testid="refund-nothing">
            订单已全额退款
          </p>
        ) : (
          <>
            <div role="radiogroup" aria-label="退款范围" className="flex gap-2">
              {SCOPES.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  role="radio"
                  aria-checked={scope === item.value}
                  // A24 的 DOM 契约：三档三个名字（`refund-scope-full` / `-lines` / `-quantities`）
                  data-testid={`refund-scope-${item.value.toLowerCase()}`}
                  onClick={() => {
                    setScope(item.value);
                    setError(null);
                  }}
                  className={`flex-1 rounded-lg border-2 border-black px-4 py-2 text-sm font-bold ${
                    scope === item.value ? 'bg-black text-white' : 'bg-white text-black'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {scope === 'FULL' ? (
              <p className="text-sm text-gray-500">将退还未退的全部商品，{AMOUNT_UNAVAILABLE_TEXT}</p>
            ) : (
              <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
                {rows.map((row) => (
                  <li
                    key={row.line.orderLineId}
                    data-testid="refund-line"
                    data-line-id={row.line.orderLineId}
                    className="flex items-center gap-3 rounded-lg border-2 border-black p-3"
                  >
                    {scope === 'LINES' && (
                      <input
                        type="checkbox"
                        aria-label={`退 ${row.line.productName}`}
                        data-testid="refund-line-check"
                        checked={checked[row.line.orderLineId] === true}
                        disabled={row.remaining <= 0}
                        onChange={(event) =>
                          setChecked((current) => ({
                            ...current,
                            [row.line.orderLineId]: event.target.checked,
                          }))
                        }
                        className="h-5 w-5"
                      />
                    )}
                    <span className="flex-1 text-sm font-bold">{row.line.productName}</span>
                    <span className="text-xs text-gray-500">货架 {row.line.shelfCode ?? '-'}</span>
                    <span className="text-xs text-gray-500">
                      {/* 行级文案的**唯一**实现（契约 §13.8）——三个参数，缺 `shortQty` 会判错可退量 */}
                      {refundLineStatusLabel(row.line.refundedQty, row.line.quantity, row.line.shortQty)}
                    </span>
                    {row.remaining <= 0 && (
                      <span className="text-xs font-bold text-gray-500" data-testid="refund-line-done">
                        已退完
                      </span>
                    )}
                    {scope === 'QUANTITIES' && (
                      <span className="flex items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          max={row.remaining}
                          aria-label={`${row.line.productName} 退款数量`}
                          data-testid="refund-line-qty"
                          data-line-id={row.line.orderLineId}
                          disabled={row.remaining <= 0}
                          // 初始为空 = 不参与；上限经由 placeholder 常驻提示（填了值之后仍看得到下面的 span）
                          placeholder={`最多可退 ${row.remaining} 件`}
                          value={quantityDrafts[row.line.orderLineId] ?? ''}
                          onChange={(event) =>
                            setQuantityDrafts((current) => ({
                              ...current,
                              [row.line.orderLineId]: event.target.value,
                            }))
                          }
                          className="h-10 w-20 rounded-lg border-2 border-black text-center"
                        />
                        <span className="text-xs text-gray-500">最多可退 {row.remaining} 件</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <Input
              label={REASON_LABEL}
              data-testid="refund-reason"
              maxLength={200}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />

            {!confirming && error !== null && (
              <p
                data-testid="refund-error"
                className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600"
              >
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onClose} disabled={busy}>
                取消
              </Button>
              <Button variant="danger" data-testid="refund-submit" onClick={openConfirm} disabled={busy}>
                退款
              </Button>
            </div>
          </>
        )}
      </div>

      {/*
        行内确认弹窗（`05-ui-design-system.md:186-190` 的模板逐字复用）：
        `max-w-sm rounded-2xl border-2 border-black bg-white p-6`，确认键 `danger`、取消键 `secondary`。
        不可逆操作必须二次确认；本组件**不用** `window.confirm`（UI 规范 `:190` 明令禁止；
        静态门 `RefundDialog.spec.tsx` ⑳ 会去掉注释后扫这个词）。
      */}
      {confirming && pending !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            data-testid="refund-confirm-dialog"
            className="flex max-w-sm flex-col gap-4 rounded-2xl border-2 border-black bg-white p-6"
          >
            <div className="text-lg font-bold">确认退款？</div>
            <div className="text-sm text-gray-500">
              {summaryOf(pending, scope, order)}。{AMOUNT_UNAVAILABLE_TEXT}。退款不可恢复。
            </div>
            {error !== null && (
              <p
                data-testid="refund-error"
                className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600"
              >
                {error}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>
                取消
              </Button>
              <Button variant="danger" data-testid="refund-confirm" onClick={() => void submit()} disabled={busy}>
                {busy ? '退款中…' : '确认退款'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
